import { useState } from "react";
import { Group, Text } from "@mantine/core";
import { Badge, SectionCard } from "@xplatform/design-system";
import { IconDownload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { ProcessStatus } from "@models/extraction";
import type { IProcessItemDetailViewModel } from "@models/extraction";
import { intelligenceEngineApi } from "@services/extractionApi";
import { AdminTable, col } from "@components/AdminTable";

interface ProcessItemsTableProps {
    items: IProcessItemDetailViewModel[];
    processId: string;
    downloadAllButton?: React.ReactNode;
}

/** The items of one extraction process, each with its input file download. */
export const ProcessItemsTable = ({ items, processId, downloadAllButton }: ProcessItemsTableProps) => {
    const { t } = useTranslation();
    const [downloadingItemId, setDownloadingItemId] = useState<string | null>(null);

    const download = async (itemId: string) => {
        setDownloadingItemId(itemId);
        try {
            const result = await intelligenceEngineApi.downloadItemInputFile(processId, itemId);
            window.open(result.s3_key, "_blank");
        } catch (err) {
            console.error("Error downloading input file:", err);
        } finally {
            setDownloadingItemId(null);
        }
    };

    const completedCount = items.filter((i) => i.status === ProcessStatus.Completed).length;
    const failedCount = items.filter((i) => i.status === ProcessStatus.Failed).length;

    const columns = [
        col.text<IProcessItemDetailViewModel>("itemId", t("admin.monitoring.processItems.itemId"), (item) => item.idShort,
                                              { width: 120, mono: true }),
        col.text<IProcessItemDetailViewModel>("fileName", t("admin.monitoring.processItems.fileName"), (item) => item.fileName,
                                              { strong: true }),
        col.status<IProcessItemDetailViewModel>("status", t("admin.monitoring.processItems.status"), (item) => item.status),
        col.date<IProcessItemDetailViewModel>("created", t("admin.monitoring.processItems.created"), (item) => item.createdAt),
        col.text<IProcessItemDetailViewModel>("error", t("admin.monitoring.processItems.error"), (item) => item.errorMessage,
                                              { dimmed: true, width: "24%" }),
        col.actions<IProcessItemDetailViewModel>("", (item) => [{
            label: t("admin.monitoring.download.downloadInput"),
            icon: <IconDownload size={16} />,
            loading: downloadingItemId === item.id,
            onClick: () => download(item.id),
        }]),
    ];

    return (
        <SectionCard
            title={
                <Group gap="sm">
                    <Text fw={600}>{t("admin.monitoring.processItems.title")}</Text>
                    <Badge size="sm" variant="light" color="blue">
                        {t("admin.monitoring.processItems.item", { count: items.length })}
                    </Badge>
                    {completedCount > 0 && (
                        <Badge size="sm" variant="light" color="green">
                            {t("admin.monitoring.processItems.completed", { count: completedCount })}
                        </Badge>
                    )}
                    {failedCount > 0 && (
                        <Badge size="sm" variant="light" color="red">
                            {t("admin.monitoring.processItems.failed", { count: failedCount })}
                        </Badge>
                    )}
                </Group>
            }
            actions={downloadAllButton}
        >
            <AdminTable
                columns={columns}
                data={items}
                getRowKey={(item) => item.id}
                emptyMessage={t("admin.monitoring.processItems.noItems")}
            />
        </SectionCard>
    );
};
